import { useState, useEffect, useRef } from 'react';
import LogoIcon from '@/components/feature/LogoIcon';

const navLinks = [
  { href: '#home', label: 'Home', icon: 'ri-home-4-line' },
  { href: '#services', label: 'Services', icon: 'ri-service-line' },
  { href: '#destinations', label: 'Destinations', icon: 'ri-earth-line' },
  { href: '#about', label: 'About', icon: 'ri-information-line' },
  { href: '#reviews', label: 'Reviews', icon: 'ri-star-line' },
  { href: '#blog', label: 'Blog', icon: 'ri-article-line' },
  { href: '#contact', label: 'Contact', icon: 'ri-mail-send-line', isCta: true },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('home');
  const navRef = useRef<HTMLElement>(null);
  const mobileMenuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    if (mobileOpen) {
      const scrollY = window.scrollY;
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = '100%';
      document.body.style.overflowY = 'hidden';
      return () => {
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.width = '';
        document.body.style.overflowY = '';
        window.scrollTo(0, scrollY);
      };
    }
  }, [mobileOpen]);

  useEffect(() => {
    if (!mobileOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMobileOpen(false);
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [mobileOpen]);

  useEffect(() => {
    if (!mobileOpen) return;
    const handleOutside = (e: MouseEvent | TouchEvent) => {
      const target = e.target as Node;
      if (mobileMenuRef.current?.contains(target)) return;
      const toggleBtn = navRef.current?.querySelector('[aria-expanded]');
      if (toggleBtn?.contains(target)) return;
      setMobileOpen(false);
    };
    document.addEventListener('mousedown', handleOutside);
    document.addEventListener('touchstart', handleOutside, { passive: true });
    return () => {
      document.removeEventListener('mousedown', handleOutside);
      document.removeEventListener('touchstart', handleOutside);
    };
  }, [mobileOpen]);

  useEffect(() => {
    const sectionIds = navLinks.map(link => link.href.replace('#', ''));
    const allIds = [...new Set([...sectionIds, 'why-us'])];
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries.filter(e => e.isIntersecting);
        if (visible.length > 0) {
          visible.sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
          setActiveSection(visible[0].target.id);
        }
      },
      { rootMargin: '-20% 0px -60% 0px', threshold: 0 }
    );
    allIds.forEach(id => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, []);

  return (
    <>
      <nav
        ref={navRef}
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          scrolled ? 'bg-background-50/95 backdrop-blur-md' : 'bg-transparent'
        }`}
        style={{ paddingTop: 'var(--safe-area-top)' }}
        role="navigation"
        aria-label="Main navigation"
      >
        <div className="w-full px-4 sm:px-6 md:px-8 lg:px-16 3xl:max-w-[1800px] 3xl:mx-auto tv:max-w-[3000px] tv:mx-auto">
          <div className="flex items-center justify-between h-16 md:h-20 lg:h-24">
            {/* Logo */}
            <a
              href="#home"
              className="flex items-center flex-shrink-0 transition-all duration-500"
              aria-label="Lucky Holidays Home"
            >
              <div className="flex items-center justify-center h-full w-[160px] sm:w-[180px] md:w-[220px] lg:w-[260px] p-0">
                <LogoIcon className="h-[70%] w-auto mx-auto" />
              </div>
            </a>

            {/* Desktop Nav */}
            <div className="hidden lg:flex items-center">
              <div className="flex items-center rounded-full px-1 py-1 bg-background-100/80">
                {navLinks.map((link) => {
                  const sectionId = link.href.replace('#', '');
                  const isActive = activeSection === sectionId;
                  return (
                    <a
                      key={link.href}
                      href={link.href}
                      className={`flex items-center gap-1.5 px-3 xl:px-4 py-2 text-xs xl:text-sm font-medium rounded-full transition-all duration-300 whitespace-nowrap ${
                        isActive
                          ? 'bg-accent-500 text-white'
                          : link.isCta
                            ? 'bg-primary-500 text-background-50'
                            : 'text-foreground-600 hover:text-foreground-900 hover:bg-background-200/70'
                      }`}
                      aria-current={isActive ? 'page' : undefined}
                    >
                      <i className={`${link.icon} text-[10px] xl:text-xs`}></i>
                      {link.label}
                    </a>
                  );
                })}
              </div>
            </div>

            {/* Mobile Hamburger */}
            <button
              className="lg:hidden flex items-center justify-center w-11 h-11 rounded-xl text-foreground-800 bg-transparent active:bg-background-200/90 active:scale-90 transition-all duration-200 cursor-pointer select-none touch-manipulation"
              style={{ WebkitTapHighlightColor: 'transparent' }}
              aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
              aria-expanded={mobileOpen}
              onClick={() => setMobileOpen(!mobileOpen)}
            >
              <i className={`text-xl transition-transform duration-300 ${mobileOpen ? 'ri-close-line rotate-90' : 'ri-menu-line'}`} aria-hidden="true"></i>
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile Overlay */}
      {mobileOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black/40 backdrop-blur-sm z-40"
          style={{ top: 'calc(var(--safe-area-top) + 64px)' }}
          onClick={() => setMobileOpen(false)}
          aria-hidden="true"
        />
      )}

      {/* Mobile Menu Panel */}
      <div
        ref={mobileMenuRef}
        className={`lg:hidden fixed left-0 right-0 z-40 bg-background-50/98 backdrop-blur-md border-t border-background-200 shadow-xl transition-all duration-300 ease-out ${
          mobileOpen ? 'top-[calc(var(--safe-area-top)+64px)] opacity-100 visible' : 'top-[calc(var(--safe-area-top)+48px)] opacity-0 invisible'
        }`}
        role="menu"
      >
        <div className="px-4 py-3 max-h-[70vh] overflow-y-auto">
          {navLinks.map((link) => {
            const sectionId = link.href.replace('#', '');
            const isActive = activeSection === sectionId;
            return (
              <a
                key={link.href}
                href={link.href}
                onClick={() => setMobileOpen(false)}
                className={`flex items-center gap-3 px-3 py-3 rounded-xl select-none transition-all active:scale-[0.97] touch-manipulation ${
                  isActive
                    ? 'bg-accent-100/70 text-accent-700'
                    : link.isCta
                      ? 'bg-primary-100/70 text-primary-700'
                      : 'text-foreground-700 active:bg-background-100'
                }`}
                style={{ WebkitTapHighlightColor: 'transparent' }}
                role="menuitem"
                aria-current={isActive ? 'page' : undefined}
              >
                <span className={`w-9 h-9 flex items-center justify-center rounded-lg ${
                  isActive
                    ? 'bg-accent-200/80 text-accent-600'
                    : link.isCta
                      ? 'bg-primary-200/80 text-primary-600'
                      : 'bg-background-100 text-foreground-400'
                }`}>
                  <i className={`${link.icon} text-base`} aria-hidden="true"></i>
                </span>
                <span className="text-sm font-medium">{link.label}</span>
                <span className="ml-auto text-foreground-300">
                  <i className="ri-arrow-right-s-line text-lg"></i>
                </span>
              </a>
            );
          })}
        </div>
      </div>
    </>
  );
}